import React, { useEffect, useState, useRef } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Loader2, Trophy, Flame, Droplets, Moon, Target, TrendingUp, Calendar, Award, Zap, Share2, Shield, Utensils, Brain } from 'lucide-react';
import { useLanguage, api } from '../context/AppContext';
import { toPng } from 'html-to-image';
import { toast } from 'sonner';
import { AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer } from 'recharts';

const phaseColors = {
  menstrual: 'text-red-400', follicular: 'text-emerald-400',
  ovulation: 'text-amber-400', luteal: 'text-purple-400', pre_menstrual: 'text-pink-400',
};

export default function WeeklyRecap() {
  const { language } = useLanguage();
  const [recap, setRecap] = useState(null);
  const [loading, setLoading] = useState(true);
  const [progression, setProgression] = useState(null);
  const [resilience, setResilience] = useState(null);
  const [history, setHistory] = useState([]);
  const [showHistory, setShowHistory] = useState(false);
  const [sharing, setSharing] = useState(false);
  const shareCardRef = useRef(null);

  useEffect(() => {
    Promise.all([
      api.get('/recap/weekly').then(r => r.data).catch(() => null),
      api.get('/recap/progression').then(r => r.data).catch(() => null),
      api.get('/recap/resilience').then(r => r.data).catch(() => null),
    ]).then(([recapData, progData, resData]) => {
      setRecap(recapData);
      setProgression(progData);
      setResilience(resData);
    }).finally(() => setLoading(false));
  }, []);

  const handleShare = async () => {
    if (!shareCardRef.current) return;
    setSharing(true);
    try {
      const dataUrl = await toPng(shareCardRef.current, { pixelRatio: 3, cacheBust: true, backgroundColor: '#0a0a1a' });
      if (navigator.share && navigator.canShare) {
        const blob = await (await fetch(dataUrl)).blob();
        const file = new File([blob], 'yumix-recap.png', { type: 'image/png' });
        if (navigator.canShare({ files: [file] })) {
          await navigator.share({ files: [file], title: 'YUMIX Récap', text: 'Mon récap hebdomadaire YUMIX!' });
          toast.success(language === 'fr' ? 'Partagé!' : 'Shared!');
          setSharing(false);
          return;
        }
      }
      const link = document.createElement('a');
      link.download = `yumix-recap-${recap?.week_end || 'week'}.png`;
      link.href = dataUrl;
      link.click();
      toast.success(language === 'fr' ? 'Image téléchargée!' : 'Image downloaded!');
    } catch { toast.error(language === 'fr' ? 'Erreur' : 'Error'); }
    finally { setSharing(false); }
  };

  if (loading) return (
    <div className="flex items-center justify-center min-h-[400px]">
      <div className="text-center">
        <Loader2 className="w-8 h-8 text-primary animate-spin mx-auto mb-3" />
        <p className="text-muted-foreground text-sm">{language === 'fr' ? 'Génération du récap...' : 'Generating recap...'}</p>
      </div>
    </div>
  );

  if (!recap || recap.error) return (
    <div className="text-center py-16">
      <p className="text-muted-foreground">{language === 'fr' ? 'Récap non disponible.' : 'Recap not available.'}</p>
    </div>
  );

  const { stats, leaderboard: lb, prs, challenge, cycle, motivation } = recap;
  const streak = progression?.streak || 0;
  const weeks = progression?.weeks || [];
  const currentIndex = progression?.current_index || 0;
  const retention = progression?.retention || null;

  return (
    <div className="space-y-5 pb-24" data-testid="weekly-recap-page">
      {/* Header */}
      <div className="text-center space-y-2">
        <h1 className="font-heading text-2xl sm:text-3xl uppercase tracking-tight text-foreground">
          {language === 'fr' ? 'Récap Hebdomadaire' : 'Weekly Recap'}
        </h1>
        <p className="text-xs text-muted-foreground">{recap.week_start} → {recap.week_end}</p>
      </div>

      {/* Grade + Streak Row */}
      <div className="flex items-center justify-center gap-4 flex-wrap">
        {lb?.grade && (
          <div className="bg-gradient-to-r from-violet-600 to-blue-600 px-6 py-2.5 rounded-full text-center" data-testid="recap-grade-badge">
            <span className="text-white font-heading text-lg tracking-widest">{lb.grade}</span>
            <span className="block text-violet-200 text-[10px]">#{lb.rank} / {lb.total_users}</span>
          </div>
        )}
        {streak > 0 && (
          <div className="bg-gradient-to-r from-orange-600 to-red-600 px-5 py-2.5 rounded-full text-center" data-testid="streak-badge">
            <span className="text-white font-heading text-lg tracking-wide">{streak}</span>
            <span className="block text-orange-200 text-[10px]">
              {language === 'fr' ? (streak === 1 ? 'semaine' : 'semaines') : (streak === 1 ? 'week' : 'weeks')}
            </span>
          </div>
        )}
      </div>

      {/* Courbe EXTRA MILES — Discipline Index */}
      {weeks.length > 0 && (
        <Card className="bg-card border-border" data-testid="discipline-curve">
          <CardHeader className="pb-1">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2">
                <TrendingUp className="w-4 h-4 text-violet-400" />
                {language === 'fr' ? 'Courbe EXTRA MILES' : 'EXTRA MILES Curve'}
              </span>
              <span className="text-xs text-muted-foreground font-normal">
                {language === 'fr' ? 'Indice de Discipline' : 'Discipline Index'}
              </span>
            </CardTitle>
          </CardHeader>
          <CardContent className="pb-3">
            <div className="h-40">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={weeks} margin={{ top: 5, right: 5, left: -20, bottom: 0 }}>
                  <defs>
                    <linearGradient id="disciplineGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop offset="5%" stopColor="#8b5cf6" stopOpacity={0.4} />
                      <stop offset="95%" stopColor="#8b5cf6" stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <XAxis dataKey="week_label" tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
                  <YAxis domain={[0, 100]} tick={{ fontSize: 10, fill: '#71717a' }} axisLine={false} tickLine={false} />
                  <Tooltip
                    contentStyle={{ background: '#1a1a2e', border: '1px solid #2d2d4a', borderRadius: 8, fontSize: 12 }}
                    labelStyle={{ color: '#a78bfa' }}
                    formatter={(v) => [`${v}/100`, language === 'fr' ? 'Discipline' : 'Discipline']}
                  />
                  <Area type="monotone" dataKey="discipline_index" stroke="#8b5cf6" strokeWidth={2.5} fill="url(#disciplineGrad)" dot={{ r: 4, fill: '#8b5cf6', strokeWidth: 0 }} activeDot={{ r: 6, fill: '#a78bfa' }} />
                </AreaChart>
              </ResponsiveContainer>
            </div>
            <div className="flex items-center justify-between mt-2">
              <span className="text-xs text-muted-foreground">{language === 'fr' ? 'Score actuel' : 'Current score'}</span>
              <span className={`text-lg font-bold ${currentIndex >= 70 ? 'text-green-400' : currentIndex >= 40 ? 'text-amber-400' : 'text-red-400'}`}>
                {currentIndex}/100
              </span>
            </div>
          </CardContent>
        </Card>
      )}

      {/* AI Motivation */}
      {motivation && (
        <Card className="bg-gradient-to-br from-violet-950/40 to-card border-l-4 border-l-violet-500" data-testid="recap-motivation">
          <CardContent className="py-5 px-5">
            <p className="text-foreground/90 text-sm leading-relaxed italic">"{motivation}"</p>
            <p className="text-violet-400 text-xs mt-3 text-right">— YUMIX</p>
          </CardContent>
        </Card>
      )}

      {/* Stats Grid */}
      <div className="grid grid-cols-3 gap-2.5" data-testid="recap-stats-grid">
        <SC icon={<Flame className="w-4 h-4" />} label={language === 'fr' ? 'Repas' : 'Meals'} value={stats.meals_count} color="text-green-400" />
        <SC icon={<Zap className="w-4 h-4" />} label="Cal/j" value={stats.avg_daily_calories} color="text-amber-400" />
        <SC icon={<TrendingUp className="w-4 h-4" />} label="Prot/j" value={`${stats.avg_daily_protein_g}g`} color="text-blue-400" />
        <SC icon={<Trophy className="w-4 h-4" />} label="Workouts" value={stats.workout_count} color="text-red-400" />
        <SC icon={<Droplets className="w-4 h-4" />} label="Eau/j" value={`${stats.water_avg_daily_ml}ml`} color="text-cyan-400" />
        <SC icon={<Calendar className="w-4 h-4" />} label={language === 'fr' ? 'Actifs' : 'Active'} value={`${stats.active_days}/7`} color="text-violet-400" />
      </div>

      {stats.avg_sleep_hours > 0 && (
        <Card className="bg-card border-border">
          <CardContent className="py-3 px-4 flex items-center justify-between">
            <div className="flex items-center gap-2"><Moon className="w-4 h-4 text-indigo-400" /><span className="text-sm text-muted-foreground">Sommeil</span></div>
            <span className="text-lg font-bold text-indigo-400">{stats.avg_sleep_hours}h</span>
          </CardContent>
        </Card>
      )}

      {/* Triple Threat */}
      {lb && (
        <Card className="bg-card border-border" data-testid="recap-triple-threat">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2"><Trophy className="w-4 h-4 text-violet-400" />TRIPLE MENACE — {lb.total_pts} pts</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-3 gap-2">
              <PtsBox label="Constance" pts={lb.constancy_pts} color="green" />
              <PtsBox label="Performance" pts={lb.performance_pts} color="amber" />
              <PtsBox label="Engagement" pts={lb.engagement_pts} color="blue" />
            </div>
          </CardContent>
        </Card>
      )}

      {prs?.length > 0 && (
        <Card className="bg-card border-border" data-testid="recap-prs">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Award className="w-4 h-4 text-amber-400" />RECORDS PERSONNELS</CardTitle></CardHeader>
          <CardContent>
            {prs.map((pr, i) => (
              <div key={`pr-${pr.exercise}-${i}`} className="flex items-center justify-between py-1.5 border-b border-border/50 last:border-0">
                <span className="text-sm text-foreground">{pr.exercise}</span>
                <span className="text-sm font-bold text-amber-400">{pr.weight}lbs x{pr.reps}</span>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      {challenge?.description && (
        <Card className="bg-card border-border" data-testid="recap-challenge">
          <CardHeader className="pb-2"><CardTitle className="text-sm flex items-center gap-2"><Target className="w-4 h-4 text-violet-400" />DÉFI ADAPTATIF</CardTitle></CardHeader>
          <CardContent>
            <p className="text-sm text-muted-foreground">{challenge.description}</p>
            <span className={`inline-block mt-2 px-2 py-0.5 rounded-full text-xs font-bold ${challenge.status === 'completed' ? 'bg-green-500/20 text-green-400' : 'bg-blue-500/20 text-blue-400'}`}>
              {challenge.status === 'completed' ? 'Complété' : 'En cours'}
            </span>
          </CardContent>
        </Card>
      )}

      {cycle?.phase && (
        <Card className="bg-card border-border" data-testid="recap-cycle">
          <CardContent className="py-3 px-4">
            <div className="flex items-center justify-between">
              <span className={`text-sm font-semibold ${phaseColors[cycle.phase] || 'text-pink-400'}`}>Phase {cycle.label_fr}</span>
              {cycle.calorie_bonus > 0 && <span className="text-xs text-orange-400">+{cycle.calorie_bonus} kcal</span>}
            </div>
            {cycle.nutrition_focus && <span className="inline-block mt-1.5 px-2 py-0.5 rounded-full bg-green-500/15 text-green-400 text-[10px] font-bold uppercase">{cycle.nutrition_focus}</span>}
          </CardContent>
        </Card>
      )}

      {/* Retention Challenges (J8+) Summary */}
      {retention?.active && (
        <Card className="bg-card border-border" data-testid="recap-retention">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center justify-between">
              <span className="flex items-center gap-2"><Shield className="w-4 h-4 text-emerald-400" />{language === 'fr' ? 'Défis de Rétention' : 'Retention Challenges'}</span>
              <span className="text-xs font-bold text-emerald-400">{retention.total_points} pts</span>
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-2">
            {Object.entries(retention.pillars || {}).map(([key, p]) => (
              <div key={key} className="flex items-center justify-between py-1.5 px-2 rounded bg-secondary/30">
                <div className="flex items-center gap-2">
                  {key === 'steel_wall' && <Shield className="w-3.5 h-3.5 text-green-400" />}
                  {key === 'metronome' && <Utensils className="w-3.5 h-3.5 text-blue-400" />}
                  {key === 'reservoir' && <Droplets className="w-3.5 h-3.5 text-cyan-400" />}
                  <span className="text-xs text-foreground">{p.name_fr}</span>
                </div>
                <div className="flex items-center gap-2">
                  <span className="text-[10px] text-muted-foreground">{p.progress}%</span>
                  <span className={`text-[10px] font-bold ${p.completed ? 'text-green-400' : 'text-muted-foreground'}`}>
                    {p.completed ? `+${p.points}` : '—'}
                  </span>
                </div>
              </div>
            ))}
            {retention.special_mission && (
              <div className="flex items-center justify-between py-1.5 px-2 rounded bg-amber-500/5 border border-amber-500/20">
                <div className="flex items-center gap-2">
                  <Zap className="w-3.5 h-3.5 text-amber-400" />
                  <span className="text-xs text-foreground">{retention.special_mission.name_fr || 'Mission Spéciale'}</span>
                </div>
                <span className={`text-[10px] font-bold ${retention.special_mission.completed ? 'text-amber-400' : 'text-muted-foreground'}`}>
                  {retention.special_mission.completed ? `+${retention.special_mission.points}` : '—'}
                </span>
              </div>
            )}
            <p className="text-[10px] text-center text-muted-foreground pt-1">{retention.completed_count}/4 {language === 'fr' ? 'complétés' : 'completed'}</p>
          </CardContent>
        </Card>
      )}

      {/* ===== BILAN MENTAL — Score de Résilience ===== */}
      {resilience && (
        <Card className="bg-gradient-to-br from-violet-950/40 via-card to-indigo-950/20 border-0 shadow-xl overflow-hidden" data-testid="resilience-section">
          <CardHeader className="pb-2">
            <CardTitle className="text-sm flex items-center gap-2">
              <Brain className="w-4 h-4 text-violet-400" />
              {language === 'fr' ? 'Bilan Mental' : 'Mental Report'}
            </CardTitle>
          </CardHeader>
          <CardContent className="space-y-4">
            {/* Resilience Gauge */}
            <div className="flex items-center gap-5">
              <div className="relative w-24 h-24 flex-shrink-0">
                <svg viewBox="0 0 100 100" className="w-full h-full transform -rotate-90">
                  <circle cx="50" cy="50" r="42" fill="none" stroke="rgba(255,255,255,0.04)" strokeWidth="8" />
                  <circle
                    cx="50" cy="50" r="42" fill="none"
                    stroke={resilience.resilience_score >= 70 ? '#a78bfa' : resilience.resilience_score >= 40 ? '#f59e0b' : '#6b7280'}
                    strokeWidth="8" strokeLinecap="round"
                    strokeDasharray={`${(resilience.resilience_score / 100) * 264} 264`}
                    style={{ transition: 'stroke-dasharray 1.2s ease-out' }}
                  />
                </svg>
                <div className="absolute inset-0 flex flex-col items-center justify-center">
                  <span className="text-2xl font-black" data-testid="resilience-score">{resilience.resilience_score}</span>
                  <span className="text-[8px] text-muted-foreground uppercase tracking-widest">/100</span>
                </div>
              </div>
              <div className="flex-1 space-y-1.5">
                <p className="text-xs font-heading uppercase tracking-wider text-violet-400" data-testid="resilience-label">
                  {language === 'fr' ? 'Indice de la Citadelle' : 'Citadel Index'}
                </p>
                <div className="grid grid-cols-2 gap-x-3 gap-y-1">
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-violet-400" />
                    <span className="text-[10px] text-muted-foreground">{resilience.total_sessions} session{resilience.total_sessions !== 1 ? 's' : ''}</span>
                  </div>
                  <div className="flex items-center gap-1.5">
                    <div className="w-1.5 h-1.5 rounded-full bg-emerald-400" />
                    <span className="text-[10px] text-muted-foreground" data-testid="reframe-count">{resilience.positive_reframes} recadrage{resilience.positive_reframes !== 1 ? 's' : ''}</span>
                  </div>
                </div>
                {/* Theme pills */}
                {resilience.top_themes?.length > 0 && (
                  <div className="flex flex-wrap gap-1 pt-1">
                    {resilience.top_themes.map((t) => (
                      <span key={t.theme} className="px-2 py-0.5 rounded-full bg-violet-500/10 text-violet-300 text-[9px] font-medium" data-testid={`theme-${t.theme}`}>
                        {t.label_fr}
                      </span>
                    ))}
                  </div>
                )}
              </div>
            </div>

            {/* AI Summary */}
            {resilience.ai_summary && (
              <div className="p-3.5 rounded-xl bg-white/[0.03] border border-violet-500/15" data-testid="resilience-summary">
                <p className="text-sm text-foreground/90 leading-relaxed italic">"{resilience.ai_summary}"</p>
                <p className="text-violet-400 text-[10px] mt-2 text-right uppercase tracking-wider">Score de Résilience</p>
              </div>
            )}

            {/* Score Breakdown mini-bar */}
            {resilience.breakdown && (
              <div className="flex gap-1 h-2 rounded-full overflow-hidden bg-white/[0.03]" data-testid="resilience-breakdown">
                <div className="bg-violet-500 rounded-l-full" style={{ width: `${resilience.breakdown.engagement}%` }} title="Engagement" />
                <div className="bg-emerald-500" style={{ width: `${resilience.breakdown.reframes}%` }} title="Recadrages" />
                <div className="bg-amber-500" style={{ width: `${resilience.breakdown.diversity}%` }} title="Diversité" />
                <div className="bg-blue-500 rounded-r-full" style={{ width: `${resilience.breakdown.consistency}%` }} title="Régularité" />
              </div>
            )}
          </CardContent>
        </Card>
      )}

      {/* Share Button */}
      <Button onClick={handleShare} disabled={sharing} className="w-full bg-gradient-to-r from-violet-600 to-blue-600 hover:from-violet-700 hover:to-blue-700 text-white h-12 text-base font-semibold" data-testid="share-recap-btn">
        {sharing ? <Loader2 className="w-5 h-5 mr-2 animate-spin" /> : <Share2 className="w-5 h-5 mr-2" />}
        {language === 'fr' ? 'Partager mon succès' : 'Share My Success'}
      </Button>

      {/* ===== SHAREABLE IMAGE (off-screen, captured by html-to-image) ===== */}
      <div style={{ position: 'absolute', left: '-9999px', top: 0 }}>
        <div ref={shareCardRef} style={{ width: 1080, minHeight: 1350, background: 'linear-gradient(180deg, #0a0a1a 0%, #1a1040 50%, #0a0a1a 100%)', padding: 64, fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif', color: '#fff' }}>
          {/* Logo */}
          <div style={{ textAlign: 'center', marginBottom: 32 }}>
            <div style={{ fontSize: 48, fontWeight: 900, letterSpacing: 8 }}>EXTRA MILES</div>
            <div style={{ fontSize: 16, color: '#a78bfa', letterSpacing: 4, marginTop: 4 }}>YUMIX COACHING</div>
          </div>

          {/* Grade + Streak */}
          <div style={{ display: 'flex', justifyContent: 'center', gap: 20, marginBottom: 32 }}>
            {lb?.grade && (
              <div style={{ background: 'linear-gradient(135deg, #7c3aed, #3b82f6)', padding: '14px 40px', borderRadius: 60, textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 900, letterSpacing: 6 }}>{lb.grade}</div>
                <div style={{ fontSize: 13, color: '#c4b5fd', marginTop: 2 }}>#{lb.rank} / {lb.total_users}</div>
              </div>
            )}
            {streak > 0 && (
              <div style={{ background: 'linear-gradient(135deg, #ea580c, #dc2626)', padding: '14px 36px', borderRadius: 60, textAlign: 'center' }}>
                <div style={{ fontSize: 32, fontWeight: 900 }}>{streak}</div>
                <div style={{ fontSize: 13, color: '#fed7aa', marginTop: 2 }}>{streak === 1 ? 'semaine' : 'semaines'}</div>
              </div>
            )}
          </div>

          {/* User + Week */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 24, fontWeight: 700, color: '#e5e5e5' }}>{recap.user_name}</div>
            <div style={{ fontSize: 13, color: '#71717a', marginTop: 4 }}>Semaine du {recap.week_start} → {recap.week_end}</div>
          </div>

          {/* Discipline Index */}
          <div style={{ textAlign: 'center', marginBottom: 24 }}>
            <div style={{ fontSize: 13, color: '#71717a', letterSpacing: 2, marginBottom: 8 }}>INDICE DE DISCIPLINE</div>
            <div style={{ fontSize: 64, fontWeight: 900, color: currentIndex >= 70 ? '#22c55e' : currentIndex >= 40 ? '#f59e0b' : '#ef4444' }}>{currentIndex}</div>
            <div style={{ fontSize: 14, color: '#52525b' }}>/100</div>
          </div>

          {/* Mini Curve (SVG bars as approximation for image) */}
          {weeks.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'flex-end', gap: 16, height: 120, marginBottom: 32, padding: '0 40px' }}>
              {weeks.map((w, i) => (
                <div key={`week-${w.week_label}-${i}`} style={{ flex: 1, textAlign: 'center' }}>
                  <div style={{ background: `linear-gradient(to top, #7c3aed, ${w.discipline_index >= 70 ? '#22c55e' : w.discipline_index >= 40 ? '#f59e0b' : '#ef4444'})`, height: `${Math.max(w.discipline_index, 8)}%`, borderRadius: 8, marginBottom: 6, minHeight: 8 }} />
                  <div style={{ fontSize: 11, color: '#71717a' }}>{w.week_label}</div>
                  <div style={{ fontSize: 14, fontWeight: 700, color: '#a78bfa' }}>{w.discipline_index}</div>
                </div>
              ))}
            </div>
          )}

          {/* Stats */}
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 14, marginBottom: 24 }}>
            <ShareStat label="REPAS" value={stats.meals_count} color="#22c55e" />
            <ShareStat label="CAL/JOUR" value={stats.avg_daily_calories} color="#f59e0b" />
            <ShareStat label="PROT/JOUR" value={`${stats.avg_daily_protein_g}g`} color="#3b82f6" />
            <ShareStat label="WORKOUTS" value={stats.workout_count} color="#ef4444" />
            <ShareStat label="EAU/JOUR" value={`${stats.water_avg_daily_ml}ml`} color="#06b6d4" />
            <ShareStat label="ACTIFS" value={`${stats.active_days}/7`} color="#a78bfa" />
          </div>

          {/* Triple Threat */}
          {lb && (
            <div style={{ background: 'rgba(255,255,255,0.05)', borderRadius: 16, padding: 20, marginBottom: 20 }}>
              <div style={{ fontSize: 13, color: '#a78bfa', fontWeight: 700, letterSpacing: 2, marginBottom: 12 }}>TRIPLE MENACE — {lb.total_pts} PTS</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {[['CONSTANCE', lb.constancy_pts, '#22c55e'], ['PERFORMANCE', lb.performance_pts, '#f59e0b'], ['ENGAGEMENT', lb.engagement_pts, '#3b82f6']].map(([l, v, c]) => (
                  <div key={l} style={{ flex: 1, background: `${c}15`, borderRadius: 12, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: c, letterSpacing: 1 }}>{l}</div>
                    <div style={{ fontSize: 24, fontWeight: 900, color: c }}>{v}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Cycle */}
          {cycle?.phase && (
            <div style={{ background: 'rgba(236,72,153,0.08)', borderRadius: 16, padding: 16, marginBottom: 20, borderLeft: '4px solid #ec4899' }}>
              <span style={{ fontSize: 14, fontWeight: 700, color: '#ec4899' }}>Phase {cycle.label_fr}</span>
              {cycle.calorie_bonus > 0 && <span style={{ fontSize: 13, color: '#fb923c', marginLeft: 12 }}>+{cycle.calorie_bonus} kcal</span>}
            </div>
          )}

          {/* Retention Challenges in share image */}
          {retention?.active && (
            <div style={{ background: 'rgba(16,185,129,0.08)', borderRadius: 16, padding: 20, marginBottom: 20, borderLeft: '4px solid #10b981' }}>
              <div style={{ fontSize: 13, color: '#10b981', fontWeight: 700, letterSpacing: 2, marginBottom: 12 }}>DÉFIS DE RÉTENTION — {retention.total_points} PTS</div>
              <div style={{ display: 'flex', gap: 10 }}>
                {Object.entries(retention.pillars || {}).map(([key, p]) => (
                  <div key={key} style={{ flex: 1, background: p.completed ? 'rgba(34,197,94,0.1)' : 'rgba(255,255,255,0.03)', borderRadius: 12, padding: 14, textAlign: 'center' }}>
                    <div style={{ fontSize: 10, color: p.completed ? '#22c55e' : '#71717a', letterSpacing: 1 }}>{p.name_fr}</div>
                    <div style={{ fontSize: 20, fontWeight: 900, color: p.completed ? '#22c55e' : '#52525b' }}>{p.completed ? `+${p.points}` : `${p.progress}%`}</div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Bilan Mental in share image */}
          {resilience && resilience.resilience_score > 0 && (
            <div style={{ background: 'rgba(139,92,246,0.08)', borderRadius: 16, padding: 20, marginBottom: 20, borderLeft: '4px solid #8b5cf6' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: 16, marginBottom: 12 }}>
                <div style={{ fontSize: 13, color: '#a78bfa', fontWeight: 700, letterSpacing: 2 }}>BILAN MENTAL</div>
                <div style={{ fontSize: 32, fontWeight: 900, color: resilience.resilience_score >= 70 ? '#a78bfa' : '#f59e0b' }}>{resilience.resilience_score}<span style={{ fontSize: 14, color: '#52525b' }}>/100</span></div>
              </div>
              {resilience.ai_summary && (
                <div style={{ fontSize: 13, color: '#d1d5db', fontStyle: 'italic', lineHeight: 1.5 }}>"{resilience.ai_summary}"</div>
              )}
              <div style={{ display: 'flex', gap: 12, marginTop: 12 }}>
                <div style={{ fontSize: 11, color: '#a78bfa' }}>{resilience.total_sessions} sessions</div>
                <div style={{ fontSize: 11, color: '#34d399' }}>{resilience.positive_reframes} recadrages</div>
              </div>
            </div>
          )}

          {/* Footer */}
          <div style={{ textAlign: 'center', marginTop: 24, paddingTop: 20, borderTop: '1px solid rgba(255,255,255,0.08)' }}>
            <div style={{ fontSize: 11, color: '#52525b', letterSpacing: 2 }}>LA SCIENCE DE LA DISCIPLINE RENCONTRE LA TECHNOLOGIE</div>
            <div style={{ fontSize: 10, color: '#3f3f46', marginTop: 6 }}>YUMIX — Programme Extra Miles</div>
          </div>
        </div>
      </div>

      {/* History */}
      <div className="text-center pt-2">
        {!showHistory ? (
          <Button variant="ghost" size="sm" onClick={() => { setShowHistory(true); api.get('/recap/history').then(r => setHistory(r.data.recaps || [])).catch(() => {}); }} className="text-muted-foreground text-xs" data-testid="show-recap-history-btn">
            {language === 'fr' ? 'Voir les récaps précédents' : 'View past recaps'}
          </Button>
        ) : (
          <div className="space-y-3">
            <h3 className="text-sm font-semibold text-foreground">Historique</h3>
            {history.length === 0 && <p className="text-xs text-muted-foreground">Aucun récap précédent</p>}
            {history.map((r, i) => (
              <div key={`hist-${r.week_start || i}`} className="bg-secondary/50 rounded-lg p-3 text-left">
                <p className="text-xs text-muted-foreground">{r.week_start} → {r.week_end}</p>
                <p className="text-sm text-foreground mt-1">{r.stats?.meals_count} repas | {r.stats?.workout_count} workouts | {r.leaderboard?.grade}</p>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

function SC({ icon, label, value, color }) {
  return (
    <div className="bg-card border border-border rounded-lg p-3 text-center">
      <div className={`flex justify-center mb-1 ${color}`}>{icon}</div>
      <p className={`text-xl font-bold ${color}`}>{value}</p>
      <p className="text-[10px] text-muted-foreground uppercase mt-0.5">{label}</p>
    </div>
  );
}

function PtsBox({ label, pts, color }) {
  return (
    <div className={`bg-${color}-500/10 rounded-lg p-2.5 text-center`}>
      <p className={`text-[10px] text-${color}-400 uppercase`}>{label}</p>
      <p className={`text-lg font-bold text-${color}-400`}>{pts}</p>
    </div>
  );
}

function ShareStat({ label, value, color }) {
  return (
    <div style={{ flex: '1 1 calc(33.33% - 14px)', minWidth: 130, background: 'rgba(255,255,255,0.04)', borderRadius: 14, padding: 18, textAlign: 'center' }}>
      <div style={{ fontSize: 10, color: '#71717a', letterSpacing: 1 }}>{label}</div>
      <div style={{ fontSize: 32, fontWeight: 900, color, marginTop: 2 }}>{value}</div>
    </div>
  );
}
